<script>
	import { mapState } from "vuex";
	import Multiselect from "vue-multiselect";
	import i18n from "@/i18n";
	import Establishment from "@/components/ClientPicker/Establishment.vue";
	import EstablishmentContact from "@/components/ClientPicker/EstablishmentContact.vue";
	import DeliveryEstablishment from "@/components/ClientPicker/DeliveryEstablishment.vue";
	import onboardingPopoverMixin from "@/mixins/onboardingPopoverMixin";
	import SwitchToggle from "@/components/UI/SwitchToggle.vue";
	import {
		openAddressModal,
		openDeliveryAddressModal,
		openEstablishmentModal,
		resetDelivery,
		resolveContact,
		resolveEstablishment,
		saveDelivery
	} from "@/composables/useDeliveryStoreSync";

	export default {
		components: {
			Establishment,
			Multiselect,
			EstablishmentContact,
			DeliveryEstablishment,
			SwitchToggle,
		},
		mixins: [onboardingPopoverMixin],
		props: {
			isThirdAccount: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				showDeliveryAddress: false,
				deliveryEstablishment: null,
				deliveryEstablishmentContact: null,
				isSyncingDelivery: false,
				isLoading: false,
				longCustomerDisplay: false,
				customerName: null,
				$_debounceTimer: null,
				unset_contactInfo: {
					type: undefined,
					name: "",
					address: "",
					address2: "",
					location: "",
					details: "",
					new: true
				}
			};
		},
		computed: {
			...mapState({
				appId: state => state.app.id,
				rights: state => state.app.rights,
				eventContact: state => state.home.eventContact,
				contacts: state => state.contacts.contacts, // list of all ContactPerson et ContactOrganization
				showEditAddressModal: state => state.home.showEditAddressModal,
				showEditEstablishmentContactModal: state => state.home.showEditEstablishmentContactModal,
				delivery: state => state.home.delivery,
			}),
			longCustomerName() {
				return this.contactInfos?.name?.length >= 99;
			},
			contactInfos: {
				get() {
					return this.isThirdAccount ? this.$store.state.contacts.thirdAccount : this.$store.state.contacts.contactInfos;
				},
				set(value) {
					this.isThirdAccount ? this.$store.commit("thirdAccount", value) : this.$store.commit("contactInfos", value);
				}
			},
			isThirdAccountGlobal: {
				get() {
					return this.$store.state.contacts.isThirdAccountGlobal;
				},
				set(value) {
					this.$store.commit("isThirdAccountGlobal", value);
				}
			},
			contactsOrdered() {
				const contactsCopy = [...this.contacts];
				contactsCopy.map(contact => {
					const newName =
						contact.type === "person"
							? contact.lastname.toUpperCase() + (contact.firstname ? " " + contact.firstname : "")
							: contact.name.toUpperCase() + (contact.legal_form ? " " + contact.legal_form : "");
					contact["suggest"] = newName;
				});
				return contactsCopy.sort((a, b) => {
					return a.suggest.localeCompare(b.suggest);
				});
			},
			tags: {
				get() {
					return this.$store.state.tags.tags;
				},
				set(tags) {
					this.$store.commit("tags", tags);
				}
			},
			details: {
				get() {
					return this.$store.state.home.details;
				},
				set(value) {
					this.$store.commit("details", value);
				}
			},
			showDetails: {
				get() {
					return this.$store.state.home.showDetails;
				},
				set(value) {
					this.$store.commit("showDetails", value);
				}
			},
			couponCode: {
				get() {
					return this.$store.state.vat.couponCode;
				},
				set(value) {
					this.$store.commit("couponCode", value);
				}
			},
			couponCodePercent: {
				get() {
					return this.$store.state.vat.couponCodeAmount;
				},
				set(value) {
					this.$store.commit("couponCodePercent", value);
				}
			},
			couponCodeValue: {
				get() {
					return this.$store.state.vat.couponCodeValue;
				},
				set(value) {
					this.$store.commit("couponCodeValue", value);
				}
			},
			couponCodeType: {
				get() {
					return this.$store.state.vat.couponCodeType;
				},
				set(value) {
					this.$store.commit("couponCodeType", value);
				}
			},
		},
		watch: {
			showDeliveryAddress(isEnabled) {
				if (isEnabled) {
					this.syncSelections();
				}
				if (!isEnabled) {
					this.deliveryEstablishment = null;
					this.deliveryEstablishmentContact = null;
					resetDelivery(this);
				}
			},
			delivery: {
				handler() {
					this.ensureDeliveryDisplayState();
					this.syncSelections();
				},
				deep: true
			},
			showEditAddressModal(isOpen) {
				if (!isOpen && this.showDeliveryAddress) {
					this.syncSelections();
				}
			},
			showEditEstablishmentContactModal(isOpen) {
				if (!isOpen && this.showDeliveryAddress) {
					this.syncSelections();
				}
			},
			"contactInfos.establishments": {
				handler(newList, oldList) {
					if (this.showDeliveryAddress) {
						const newEstablishment = newList?.find(e => !oldList?.find(o => o.id === e.id));
						const isDelivery = this.$store.state.contacts.editableEstablishment?.is_delivery;
						if (newEstablishment && isDelivery) {
							this.$store.commit("setDeliveryFields", { establishment_id: newEstablishment.id });
						}
					}
					this.syncSelections();
				},
				deep: true
			},
			deliveryEstablishment: {
				handler(newValue) {
					this.onDeliveryChange(newValue, this.deliveryEstablishmentContact);
				},
				deep: true
			},
			deliveryEstablishmentContact: {
				handler(newValue) {
					this.onDeliveryChange(this.deliveryEstablishment, newValue);
				},
				deep: true
			},
			contactInfos: {
				handler(newObject, oldObject) {
					// newOject is empty => reset recurring recipients
					if (!newObject || !Object.keys(newObject).length) {
						this.$store.commit("resetAllRecipientsProperties");
					}

					if (newObject?.id !== oldObject?.id) {
						// Client created using the modal
						if (this.contactInfos && (this.contactInfos?.modal === true || (this.showCreatePersonModal === true || this.showCreateOrganizationModal === true))) {
							this.$bvModal.hide("modalClientCreation");
							this.selectContact(this.contactInfos, false);
						}
					}
				},
				deep: true
			},
			longCustomerName(newValue) {
				this.longCustomerDisplay = newValue;
			}
		},
		created() {
			if (this.appId === 0) {
				this.contactInfos = this.unset_contactInfo;
			}

			this.$_debounceTimer = null;
		},
		mounted() {
			this.ensureDeliveryDisplayState();
			this.syncSelections();
		},
		methods: {
			ensureDeliveryDisplayState() {
				const { establishment_id, email, address, location, name } = this.delivery;
				const hasDeliveryData = Boolean(establishment_id || email || address || location || name);
				if (hasDeliveryData && !this.showDeliveryAddress) {
					this.showDeliveryAddress = true;
				}
			},
			debounce(method, timer) {
				if (this.$_debounceTimer !== null) {
					clearTimeout(this.$_debounceTimer);
				}

				this.$_debounceTimer = setTimeout(() => {
					method();
				}, timer);
			},
			// Select a client or third
			selectContact(contact, disableEstablishmentsLoading = false) {
				const _contact = contact;
				const { discount, tags, details } = contact ?? {};

				if (details) {
					this.details = details;
					this.showDetails = true;
				}

				if (tags) this.tags = tags;

				if (discount) {
					this.couponCode = i18n.t("Remise");
					this.couponCodeType = "%";
					this.couponCodePercent = discount;
					this.couponCodeValue = discount;
				}

				if (_contact) {
					_contact.modal = false;
					// set contactInfos
					if (disableEstablishmentsLoading === false || disableEstablishmentsLoading === null) {
						const _contactInfos = JSON.parse(JSON.stringify(_contact));
						_contactInfos.establishment_id = _contact.establishments[0]?.id ?? null;

						this.contactInfos = Object.assign({}, { ..._contactInfos });

						// synchronize recurring recipients
						this.$nextTick(() => {
							this.$store.commit(
								"initRecipientEmailsFromContactInfosEstablishments",
								this.contactInfos.establishments ?? []
							);
						});
					}

					this.$store.commit(
						"paymentDelayClient",
						_contact.paymentDelayClient !== null ? parseInt(_contact.paymentDelayClient) : null
					);
					this.$store.commit("paymentMethodsClient", _contact.paymentMethodsClient);
					this.$store.commit("shlouldNnotifyPaymentMethodsPeriod", true);
				}

				return _contact;
			},
			clientsSearch(search) {
				this.debounce(async () => {
					this.isLoading = true;

					await this.$store.dispatch("relationships", { contact: null, search });

					this.isLoading = false;
				}, 300);
			},
			clear() {
				this.isLoading = false;
				this.customerName = null;

				this.$store.commit("paymentDelayClient", null);
				this.$store.commit("paymentMethodsClient", null);

				this.contactInfos = Object.assign({}, this.unset_contactInfo);

				// synchronize recurring recipients
				this.$nextTick(() => {
					this.$store.commit("resetAllRecipientsProperties");
				});

				this.$store.dispatch("relationships");
			},
			getImageLink(item) {
				if (item?.image && item.image.slice(0, 1) === "/") {
					return `https://sinao.app${item.image}`;
				} else if (item?.initials) {
					return `https://sinao.app/services/avatar/${item.initials}/${item.initials}`;
				} else if (item?.name) {
					return `https://sinao.app/services/avatar/${item.name.charAt(0)}/${item.name.charAt(0)}`;
				} else {
					return "";
				}
			},
			stringToHSLColor(inputString, s = 70, l = 60) {
				let hash = 0;

				for (let i = 0; i < inputString.length; i++) {
					hash = inputString.charCodeAt(i) + ((hash << 5) - hash);
				}

				const hue = hash % 360;
				return `hsl(${hue}, ${s}%, ${l}%)`;
			},
			modalClientCreation() {
				this.contactInfos = Object.assign({}, this.unset_contactInfo);
				this.isThirdAccountGlobal = this.isThirdAccount;
				this.$bvModal.show("modalClientCreation");
			},
			modalSinaoClientCreation(contactType) {
				// Local contact management
				if (this.eventContact !== true) {
					this.$bvModal.hide("modalClientCreation");
					this.$store.commit("setEditableContactType", contactType);
					if (contactType === "organization") {
						this.$store.commit("showCreateOrganizationModal", true);
					} else if (contactType === "person") {
						this.$store.commit("showCreatePersonModal", true);
					}
				// App contact management
				} else {
					this.dispatchEvent({
						modalName: null,
						source: "billing",
						action: "creation_client",
						data: { contactType }
					});
				}
			},
			openAddressModal() {
				openAddressModal(this, this.isThirdAccount);
			},
			openDeliveryAddressModal() {
				openDeliveryAddressModal(this, this.isThirdAccount);
			},
			onEditDeliveryAddress(establishment) {
				openEstablishmentModal(this, establishment, this.isThirdAccount);
			},
			syncSelections() {
				if (!this.showDeliveryAddress) return;

				this.isSyncingDelivery = true;
				this.deliveryEstablishment = resolveEstablishment({
					contactInfos: this.contactInfos,
					currentSelection: this.deliveryEstablishment,
					delivery: this.delivery
				});
				this.deliveryEstablishmentContact = resolveContact({
					contactInfos: this.contactInfos,
					currentSelection: this.deliveryEstablishmentContact,
					delivery: this.delivery
				});
				this.$nextTick(() => {
					this.onDeliveryChange(this.deliveryEstablishment, this.deliveryEstablishmentContact);
					this.isSyncingDelivery = false;
				});
			},
			onDeliveryChange(establishment, contact) {
				if (!this.showDeliveryAddress || this.isSyncingDelivery) return;
				if (!this.contactInfos || !Object.keys(this.contactInfos).length) return;
				saveDelivery(this, { establishment, contact });
			}
		}
	};
</script>

<template>
	<div>
		<div v-show="!longCustomerDisplay">
			<b-row style="padding: 10px 5px">
				<div style="width:100%">
					<div style="width:100%">
						<!-- Show the clients -->
						<b-col
							v-if="!contactInfos || !contactInfos.type"
							cols="12"
						>
							<Multiselect
								class="floating-model-menu multiselect__client-picker"
								:options="contactsOrdered"
								:placeholder="isThirdAccount ? $t('Sélectionner un tiers') : $t('Sélectionner un client')"
								:show-labels="false"
								label="suggest"
								track-by="suggest"
								:allow-empty="false"
								:internal-search="false"
								:loading="isLoading"
								:clear-on-select="false"
								:show-no-results="false"
								:searchable="true"
								@select="selectContact"
								@search-change="clientsSearch"
							>
								<template #noOptions>
									{{ $t("La liste est vide.") }}
								</template>

								<!-- Options on the select -->
								<template
									slot="option"
									slot-scope="props"
								>
									<div style="display: flex; align-items: center;">
										<div
											v-if="props.option.name"
											class="custom-avatar"
											:style="props.option.name ? `background-color: ${stringToHSLColor(props.option.name)}` : null"
										>
											{{ props.option.name.slice(0, 1) }}
										</div>
										<b-img-lazy
											v-else
											:src="getImageLink(props.option)"
											class="option__image"
										/>
										<div class="option__desc">
											<span class="option__title">{{ props.option["suggest"] | truncate(30) }}</span>
										</div>
									</div>
								</template>
							</Multiselect>

							<b-button
								v-if="(rights?.includes('!app.contacts.create') ? false : true) ?? true"
								size="sm"
								class="sinao-button-default"
								block
								variant="light"
								@click="modalClientCreation"
							>
								+ {{ isThirdAccount ? $t("Créer un tiers") : $t("Créer un client") }}
							</b-button>
						</b-col>

						<!-- Show the selected client -->
						<b-col
							v-else
							cols="12"
						>
							<b-row style="padding: 0 7px;">
								<b-col cols="9">
									<b-form-input
										v-model.lazy="contactInfos.name"
										placeholder="Dénomination"
										style="font-weight: bold; text-transform: uppercase; background-color: transparent;"
										class="billing__input billing__input--borderBottom billing__input--dashed"
										:disabled="!(rights?.includes('!app.contacts.create') ? false : true) ?? false"
									/>
								</b-col>
								<b-col
									cols="3"
									class="text-left pl-0"
								>
									<b-button
										v-if="appId !== 0"
										:id="'select_client' + isThirdAccount ? '_third': ''"
										style="font-size: 14px; padding-left: 0; padding-right: 0"
										variant="link"
										@click="clear"
									>
										{{ $t("Changer") }}
									</b-button>
									<b-tooltip
										placement="top"
										:target="'select_client' + isThirdAccount ? '_third': ''"
										container="body"
									>
										{{ $t("Client") }}
									</b-tooltip>
								</b-col>
							</b-row>

							<b-row class="pt-3" align-v="start">
								<b-col cols="12">
									<b-card class="address_card" style="margin: 0 7px 5px 7px; padding: 0.4rem 0.75rem;">
										<b-row>
											<Establishment
												:is-third-account="isThirdAccount"
											/>
										</b-row>
										<b-row>
											<EstablishmentContact
												v-if="isThirdAccount===false"
												:is-third-account="isThirdAccount"
											/>
										</b-row>
									</b-card>
									<b-row>
										<SwitchToggle
											style="padding: 0.4rem 1.5rem;"
											v-model="showDeliveryAddress"
											:label="$t('Ajouter une adresse de livraison différente')"
										/>
									</b-row>
									<transition name="fade">
										<b-card v-if="showDeliveryAddress" class="address_card" style="margin: 0 7px 5px 7px; padding: 0.4rem 0.75rem;">
											<b-row>
												<DeliveryEstablishment
													v-model="deliveryEstablishment"
													:establishments="contactInfos?.establishments ?? []"
													@create-address="openDeliveryAddressModal"
													@edit-address="onEditDeliveryAddress"
												/>
											</b-row>
										</b-card>
									</transition>
								</b-col>
							</b-row>
						</b-col>
					</div>
				</div>

				<!-- Client creation modal -->
				<b-modal
					id="modalClientCreation"
					:title="$t('Création de client')"
					hide-footer
					@show="onModalShow"
					@hidden="onModalHidden"
				>
					<div class="d-flex flex-column flex-sm-row gap-2" style="width: 100%">
						<s-button type="default" class="w-100" style="margin-bottom: 8px" @click="modalSinaoClientCreation('person')">
							<b-icon icon="person-fill" /> &nbsp; {{ $t("Nouveau particulier") }}
						</s-button>

						<s-button type="success" class="w-100" @click="modalSinaoClientCreation('organization')">
							<b-icon icon="building" /> &nbsp; {{ $t("Nouvelle entreprise") }}
						</s-button>
					</div>
				</b-modal>
			</b-row>
		</div>
		<div v-show="longCustomerName && longCustomerDisplay">
			<div class="p-4">
				<span v-html="$t('long customer name alert')" />
				<br>
				<b-button
					variant="link"
					@click="longCustomerDisplay=false"
					style="font-size: 13px; padding: 0; float: right"
				>
					{{ $t("Fermer") }}
				</b-button>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
	@import "../../scss/multiselect.scss";

	.establishment-details {
		font-size: 13px;
		word-break: break-all;
		text-overflow: ellipsis;
		width: 100%;
		max-width: 100%;
		overflow: hidden;
	}
	.text-gray {
		color: gray;
	}
	.dropdown-item {
		border-bottom: 1px solid var(--border);
		margin: 5px 0;
	}
	.dropdown-menu :last-child > a {
		border-bottom: 0;
		margin: 5px 0 0 0;
	}
	.bordered {
		border: 1px solid var(--border);
	}
	.rounded {
		border-radius: 7px;
	}
	.b-overlay.position-absolute {
		margin: 0 20px !important;
	}
	.notification-container {
		padding: 0.75rem;
		border: 1px solid #fff;
		border-radius: 5px;
		background-color: #fff;
		box-shadow: 0 2px 3px #f7f7f7;
	}
	.btn-xs {
		padding: 0rem 0.25rem;
		font-size: 0.8rem;
		line-height: 1.5;
		border-radius: 0.2rem;
	}
	.address_card .card-body {
		padding: 0.2rem 0.5rem 0.2rem 0.1rem;
	}
	.no-contact-data {
		font-style: italic;
		color: #bbbaba;
	}

	.modal-backdrop {
		background-color: rgb(0 0 0 / 0.2);
	}

	.custom-avatar {
		width: 26px;
		height: 26px;
		border-radius: 30px;
		color: var(--white);
		font-size: 12px;
		font-weight: 400;
		display: grid;
		justify-content: center;
		align-items: center;
		margin: 0 5px;
	}

	#modalClientCreation___BV_modal_body_ {
		display: flex;
	}

	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.3s ease;
	}

	.fade-enter,
	.fade-leave-to {
		opacity: 0;
	}
</style>
